import { useEffect, useMemo, useState } from 'react'
import type { SorterResult } from 'antd/es/table/interface'
import isNil from 'lodash/isNil'
import { getAdminSettings } from 'enrollment-platform/utils/adminSettings'
import {
  BillingProfileRecord,
  BillingProfileSortField,
  GetBillingProfilesRequestDto,
  SortOrder,
} from '../../api/dto/client-billing-profile.dto'
import { BillingProfileStatus, type PaymentType } from './types'

const SEARCH_DEBOUNCE_MS = 300

export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200]
/** Opens big enough to show every company on one page. */
const DEFAULT_PAGE_SIZE = 200

export interface BillingProfileSort {
  field: BillingProfileSortField
  order: SortOrder
}

const SORT_FIELDS = new Set<string>(Object.values(BillingProfileSortField))

const isSortField = (key: unknown): key is BillingProfileSortField =>
  typeof key === 'string' && SORT_FIELDS.has(key)

/** Reads the Table's sorter; clearing the sort returns null for the default order. */
export const toBillingProfileSort = (
  sorter:
    | SorterResult<BillingProfileRecord>
    | SorterResult<BillingProfileRecord>[]
): BillingProfileSort | null => {
  const { columnKey, order } = Array.isArray(sorter) ? sorter[0] : sorter
  if (!isSortField(columnKey) || isNil(order)) return null
  return { field: columnKey, order: order === 'descend' ? 'desc' : 'asc' }
}

/** The list's search, filters, sort and page; any change goes back to page 1. */
export const useBillingProfileFilters = () => {
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  // Removed companies are kept but hidden, so the list opens on the ones still
  // being invoiced. The status filter is how you get back to the removed ones.
  const [statusFilter, setStatusFilter] = useState<
    BillingProfileStatus | undefined
  >(BillingProfileStatus.Active)
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<
    PaymentType | undefined
  >()
  const [sort, setSort] = useState<BillingProfileSort | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search])

  const { showTestData } = getAdminSettings().finance
  const excludeTestClients = showTestData ? undefined : true
  const searchParam = debouncedSearch || undefined

  /** Everything but paging, shared by the list and the export. */
  const listFilters: Omit<GetBillingProfilesRequestDto, 'page' | 'limit'> =
    useMemo(
      () => ({
        search: searchParam,
        status: statusFilter,
        paymentType: paymentTypeFilter,
        excludeTestClients,
        ...(isNil(sort) ? {} : { sortBy: sort.field, sortOrder: sort.order }),
      }),
      [searchParam, statusFilter, paymentTypeFilter, excludeTestClients, sort]
    )

  /** Status counts follow every filter except status itself. */
  const statsFilters = useMemo(
    () => ({
      search: searchParam,
      paymentType: paymentTypeFilter,
      excludeTestClients,
    }),
    [searchParam, paymentTypeFilter, excludeTestClients]
  )

  const withFirstPage =
    <T>(set: (value: T) => void) =>
    (value: T) => {
      set(value)
      setPage(1)
    }

  /** True when the list is narrowed past the Active companies it opens on. */
  const hasNarrowedList =
    debouncedSearch.trim().length > 0 ||
    statusFilter !== BillingProfileStatus.Active ||
    !isNil(paymentTypeFilter)

  /** A new page size starts again from page 1. */
  const handlePageChange = (nextPage: number, nextPageSize: number) => {
    if (nextPageSize === pageSize) {
      setPage(nextPage)
      return
    }
    setPageSize(nextPageSize)
    setPage(1)
  }

  // Backs the action on the empty state, which is otherwise a dead end.
  const handleClearFilters = () => {
    setSearch('')
    setDebouncedSearch('')
    setStatusFilter(BillingProfileStatus.Active)
    setPaymentTypeFilter(undefined)
    setPage(1)
  }

  return {
    search,
    setSearch,
    statusFilter,
    paymentTypeFilter,
    sort,
    page,
    pageSize,
    handlePageChange,
    listFilters,
    statsFilters,
    hasNarrowedList,
    handleStatusFilter: withFirstPage(setStatusFilter),
    handlePaymentTypeFilter: withFirstPage(setPaymentTypeFilter),
    handleSortChange: withFirstPage(setSort),
    handleClearFilters,
  }
}
